import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { approveParticipants, getEventsDataById, getParticipants } from '../../../../services/CommonService';
import PaginationControls from '../../../../Utils/PaginationControls';

const Participant = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [statusData, setStatusData] = useState('');

  useEffect(() => {
    fetchEventDetails();
    fetchParticipants();
  }, []);

  const fetchEventDetails = async () => {
    try {
      const response = await getEventsDataById(id);
      setEvent(response.data);
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: err?.response?.data?.message || err?.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
    }
  };

  const fetchParticipants = async () => {
    try {
      const response = await getParticipants(id);
      setParticipants(response?.data || []);
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: err?.response?.data?.message || err?.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
    }
  };

  const exportToExcel = () => {
    if (!participants || participants.length === 0) {
      Swal.fire('No data', 'There is no event data to export.', 'warning');
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(participants.map((event, index) => ({
      "S.No.": index + 1,
      "Name": event.name,
      "Email": event.email,
      "Mobile": event.mobile,
      "Organisation": event.organisation,
      "Age": event.age,
      "Qualifications": event.qualification,
      "Nature Of Current Duties": event.nature_of_current_duties,
      "Work Area": event.work_area
    })));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Participants");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });

    saveAs(data, "Event_List.xlsx");
  };

  const handleCheckboxChange = (participantId) => {
    setSelectedParticipants(prev =>
      prev.includes(participantId)
        ? prev.filter(id => id !== participantId)
        : [...prev, participantId]
    );
  };

  const handleSaveSelected = async () => {
    try {
      const payload = {
        eventId: id,
        approved_ids: selectedParticipants
      };
      await approveParticipants(id, payload);
      Swal.fire('Success!', 'Selected participants saved.', 'success');
      fetchParticipants();
      setSelectedParticipants([]);
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: err?.response?.data?.message || err?.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const filteredParticipants = participants.filter(participant => {
    const matchesSearch =
      participant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      participant.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      participant.mobile?.includes(searchTerm);

    const matchesStatus =
      statusData === 'selected'
        ? participant.status === 1
        : statusData === 'not_selected'
          ? participant.status !== 1
          : true;
    return matchesSearch && matchesStatus;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentParticipants = filteredParticipants.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredParticipants.length / itemsPerPage);

  const dateFormat = (d) => (d ? moment(d).format('DD-MM-YYYY') : 'N/A');

  return (
    <div className="container-fluid py-4">
      <div className="card">
        <div className="card-header adminCardHeader d-flex justify-content-between align-items-center">
          <h4>Event Participants</h4>
          <button className="btn btn-sm btn-warning" onClick={() => navigate('../eventlist')}>
            <i className="fas fa-arrow-left"></i> Back
          </button>
        </div>
        <div className="card-body">
          <div className='row mb-3'>
            <div className="col-md-7">
              <h6><strong>Event Name:</strong> {event?.title || 'Loading...'}</h6>
              <h6><strong>Event Date:</strong> {dateFormat(event?.event_date)}</h6>
            </div>
            <div className="col-md-2">
              <select
                className="form-select"
                value={statusData}
                onChange={(e) => setStatusData(e.target.value)}
              >
                <option value="">All</option>
                <option value="selected">Selected</option>
                <option value="not_selected">Not Selected</option>
              </select>
            </div>
            <div className="col-md-2">
              <input
                type="text"
                className="form-control"
                placeholder="Search participants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className='col-md-1'>
              <button className="btn btn-success me-2" onClick={exportToExcel}>
                <i className="bi bi-file-earmark-excel me-1"></i> Excel
              </button>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-bordered table-hover">
              <thead className="table-primary">
                <tr>
                  <th>S.No.</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Organisation</th>
                  <th className='text-center'>
                    All <input
                      type="checkbox"
                      checked={currentParticipants.every(p => selectedParticipants.includes(p.id))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const idsToAdd = currentParticipants.filter(id => !currentParticipants.map(p => p.id)?.includes(id))?.map(p => p.id);
                          setSelectedParticipants(prev => [...new Set([...prev, ...idsToAdd])]);
                        } else {
                          setSelectedParticipants(prev => prev.filter(id => !currentParticipants.map(p => p.id).includes(id)));
                        }
                      }}
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentParticipants.length > 0 ? currentParticipants.map((participant, index) => (
                  <tr key={participant.id || index}>
                    <td>{indexOfFirstItem + index + 1}</td>
                    <td>{participant.name}</td>
                    <td>{participant.email}</td>
                    <td>{participant.mobile || 'N/A'}</td>
                    <td>{participant.organisation || 'N/A'}</td>
                    <td className='text-center'>
                      <input
                        type="checkbox"
                        disabled={participant.status === 1}
                        checked={participant.status === 1 || selectedParticipants.includes(participant.id)}
                        onChange={() => handleCheckboxChange(participant.id)}
                      />
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan="6" className="text-center">No participants found</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {selectedParticipants.length > 0 && (
            <div className="text-end mt-3">
              <button className="btn btn-primary" onClick={handleSaveSelected}>
                Save Selected ({selectedParticipants.length})
              </button>
            </div>
          )}

          {/* Pagination Controls */}
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            setCurrentPage={setCurrentPage}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            totalItems={filteredParticipants.length}
          />
        </div>
      </div>
    </div>
  );
};

export default Participant;
